import * as React from 'react';
import { Image, Text } from '@blueprint/ui';
import {
    AgencyLogo,
    CardsContainer,
    CardDetails,
    CardHeader,
    PriceContainer,
    PriceTitleText,
    MetaCard,
    StyledTitle,
    StyledCard,
    StyledLink
} from './SimilarListingCards.styles';

import { ListingFeatureList } from '@zoopla/listings/features';
import { ListingImage } from '@zoopla/listings/image';
import { SimilarListingCardProps } from './SimilarListingCards.types';

type SimilarListingCardsProps = {
    listings: SimilarListingCardProps[];
    metaCardAfter?: React.ReactElement;
    trackSimilarListingClick: (index: number) => void;
    cardContainerRef?: React.Ref<HTMLDivElement>;
    onScroll?: () => void;
};

export const SimilarListingCards = ({
    listings,
    metaCardAfter,
    trackSimilarListingClick,
    cardContainerRef,
    onScroll
}: SimilarListingCardsProps) => {
    return (
        <CardsContainer onScroll={onScroll} ref={cardContainerRef}>
            {listings.map((listing, index) => {
                const {
                    displayAddress,
                    branch,
                    featurePreview,
                    imagePreview,
                    price,
                    pricingQualifier,
                    title,
                    listingDetailUri,
                    statusSummary
                } = listing;

                return listingDetailUri ? (
                    <StyledLink
                        data-testid="similar-property-card"
                        href={listingDetailUri}
                        key={`${listing.listingId}-${index}`}
                        onClick={() => trackSimilarListingClick(index)}
                    >
                        <StyledCard hasShadow={false} hasBorder hasBackground>
                            <ListingImage
                                image={imagePreview}
                                badgeText={statusSummary}
                            />
                            <CardDetails>
                                <CardHeader>
                                    <PriceContainer>
                                        {pricingQualifier && (
                                            <PriceTitleText>
                                                {pricingQualifier}
                                            </PriceTitleText>
                                        )}
                                        <Text
                                            size="6"
                                            isBold
                                            data-testid={`price-${index}`}
                                        >
                                            {price}
                                        </Text>
                                    </PriceContainer>

                                    <AgencyLogo>
                                        {branch?.logoUrl ? (
                                            <Image
                                                src={branch?.logoUrl}
                                                alt={`Marketed by ${branch?.name}`}
                                            />
                                        ) : null}
                                    </AgencyLogo>
                                </CardHeader>
                                <ListingFeatureList features={featurePreview} />
                                {title && (
                                    <StyledTitle size="8">{title}</StyledTitle>
                                )}
                                <Text size="secondary" color={'text-body'}>
                                    {displayAddress}
                                </Text>
                            </CardDetails>
                        </StyledCard>
                    </StyledLink>
                ) : null;
            })}
            {Boolean(metaCardAfter) && (
                <MetaCard data-testid="meta-card">{metaCardAfter}</MetaCard>
            )}
        </CardsContainer>
    );
};
